import toast from 'react-hot-toast';

/**
 * 중앙 토스트 래퍼 — 기존 window.alert/confirm 대체
 * - 성공/에러/정보/로딩 토스트 + confirmAction(Promise 기반)
 */
export const toastSuccess = (message, options = {}) =>
  toast.success(message, { duration: 3000, ...options });

export const toastError = (message, options = {}) =>
  toast.error(message, { duration: 4000, ...options });

export const toastInfo = (message, options = {}) =>
  toast(message, { duration: 3000, ...options });

export const toastLoading = (message, options = {}) =>
  toast.loading(message, options);

export const toastDismiss = (toastId) => toast.dismiss(toastId);

/**
 * confirm() 대체 — 커스텀 ConfirmModal 이벤트 트리거 (Promise<boolean>).
 * App.jsx 에 <ConfirmModal /> 이 마운트되어 있어야 함.
 *
 * @param {string} message - 본문 메시지
 * @param {object} [options]
 * @param {string} [options.title='확인']
 * @param {string} [options.confirmText='확인']
 * @param {string} [options.cancelText='취소']
 * @param {'primary'|'danger'} [options.variant='primary'] - 삭제/위험 작업은 'danger' 권장
 * @returns {Promise<boolean>} true=확인, false=취소/ESC/바깥클릭
 */
export const confirmAction = (message, options = {}) => {
  // ConfirmModal이 마운트되지 않은 환경(테스트 등)에서는 window.confirm 폴백
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return Promise.resolve(window.confirm(message));
  }
  return new Promise((resolve) => {
    const event = new CustomEvent('app:confirm', {
      detail: { message, resolve, options },
    });
    window.dispatchEvent(event);
  });
};

/**
 * Phase Q+ 모바일 UX H-1 fix: window.prompt() 대체 — 커스텀 PromptModal 이벤트 트리거 (Promise<string|null>).
 * ConfirmModal 컴포넌트가 'app:prompt' 이벤트도 함께 구독.
 *
 * @param {string} message - 본문 메시지 (라벨)
 * @param {object} [options]
 * @param {string} [options.title='입력'] - 모달 타이틀
 * @param {string} [options.confirmText='확인']
 * @param {string} [options.cancelText='취소']
 * @param {string} [options.placeholder=''] - input placeholder
 * @param {string} [options.defaultValue=''] - 초기값
 * @param {number} [options.minLength=0] - 최소 길이 검증 (미달시 confirm disabled)
 * @param {boolean} [options.multiline=false] - textarea 사용 여부
 * @param {'primary'|'danger'} [options.variant='primary']
 * @returns {Promise<string|null>} 입력값 (취소/ESC/empty 시 null)
 */
export const promptAction = (message, options = {}) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
    return Promise.resolve(window.prompt(message, options.defaultValue || ''));
  }
  return new Promise((resolve) => {
    const event = new CustomEvent('app:prompt', {
      detail: { message, resolve, options },
    });
    window.dispatchEvent(event);
  });
};

/**
 * 서버 에러 응답에서 사용자 친화적 메시지 추출.
 * workflowGate.js 400 (actor_name 미입력 등) 원문 그대로 표시.
 *
 * @param {unknown} err - catch 블록의 에러 객체
 * @param {string} [fallback='요청에 실패했습니다.'] - 메시지 추출 불가 시 대체
 * @returns {string}
 */
export function extractServerError(err, fallback = '요청에 실패했습니다.') {
  return err?.response?.data?.error?.message
      || err?.response?.data?.message
      || err?.message
      || fallback;
}

export default toast;
